import { useEffect, useState } from "react";
import MainContentLayout from "../../components/mainContentLayout";
import PageHeader from "../../components/pageHeader";
import { API_BASE } from "../../config/apiConfig";
import { useToast } from "../../hooks/useToast";
import { useNavigate } from "react-router-dom";
import ActivityDropdown from "../../components/activityDropdown";

export default function PlayerApplyLeave() {
  const navigate = useNavigate();

  const [type, setType] = useState("");
  const [startDate, setStart] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEnd] = useState("");
  const [duration, setDuration] = useState(1);
  const [remark, setRemark] = useState("");
  const [emergencyReason, setEmergencyReason] = useState("");
  const [file, setFile] = useState(null);

  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState("");

  const token = sessionStorage.getItem("access_token");
  const { showToast, ToastContainer } = useToast();

  // ------------------------------------------------------------
  // LOAD LIST OF UPCOMING ACTIVITIES FOR LEAVE APPLICATION
  // ------------------------------------------------------------
  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API_BASE}/api/activities/list-all`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();
        if (data.success) setActivities(data.items);
      } catch (err) {
        console.error(err);
      }
    }
    load();
  }, []);

  // ------------------------------------------------------------
  // AUTO-COMPUTE END DATE when duration changes
  // ------------------------------------------------------------
  useEffect(() => {
    if (!startDate) return;
    const s = new Date(startDate);
    const e = new Date(s);
    e.setDate(e.getDate() + duration - 1);
    setEnd(e.toISOString().split("T")[0]);
  }, [startDate, duration]);

  // ------------------------------------------------------------
  // SUBMIT LEAVE
  // ------------------------------------------------------------
  async function submitLeave() {
    if (!type) {
      showToast("Please select a leave type", "error");
      return;
    }

    let documentUrl = "";
    let activityName = null;

    // local vars (will be overwritten for tournament/event)
    let leaveStartDate = startDate;
    let leaveEndDate = endDate;
    let leaveDuration = duration;

    // -------------------------------------------
    // 1. MEDICAL LEAVE VALIDATIONS
    // -------------------------------------------
    if (type === "Medical Leave") {

      // File required
      if (!file) {
        showToast("Medical Leave requires an MC document.", "error");
        return;
      }

      // File size limit check (700KB)
      const maxSize = 700 * 1024;
      if (file.size > maxSize) {
        showToast("File too large. Max 700 KB.", "error");
        return;
      }

      // Check for attendance conflict from backend
      const checkRes = await fetch(
        `${API_BASE}/api/check-mc-allowed?startDate=${startDate}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const checkData = await checkRes.json();

      if (!checkData.allowed) {
        showToast(checkData.message || "Cannot apply MC for this day.", "error");
        return;
      }

      // Convert file to Base64
      try {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        await new Promise((resolve, reject) => {
          reader.onload = () => {
            documentUrl = reader.result;
            resolve();
          };
          reader.onerror = reject;
        });
      } catch (err) {
        showToast("Failed to process MC file", "error");
        return;
      }
    }

    // -------------------------------------------
    // 2. OVERLAP CHECK (MC & EMERGENCY)
    // -------------------------------------------
    if (type === "Medical Leave" || type === "Emergency Leave") {
      const overlapRes = await fetch(
        `${API_BASE}/api/player/leave/check-overlap`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            type,
            startDate,
            endDate,
          }),
        }
      );

      const overlapData = await overlapRes.json();
      if (!overlapData.allowed) {
        showToast(overlapData.message, "error");
        return;
      }
    }

    // -------------------------------------------
    // 3. TOURNAMENT / EVENT VALIDATION
    // -------------------------------------------
    if (type === "Tournament Leave" || type === "Event Leave") {
      if (!selectedActivity) {
        showToast("Please select a valid activity", "error");
        return;
      }

      // Check duplicate leave for same activity
      const dupRes = await fetch(
        `${API_BASE}/api/player/leave/check-activity-duplicate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            type,
            activityId: selectedActivity,
          }),
        }
      );
      const dupData = await dupRes.json();
      if (!dupData.allowed) {
        showToast(dupData.message, "error");
        return;
      }

      // Auto-assign activity dates
      const act = activities.find((a) => a.id === selectedActivity);
      if (act) {
        activityName = act.name;
        leaveStartDate = act.startDate;
        leaveEndDate = act.endDate;

        const s = new Date(act.startDate);
        const e = new Date(act.endDate);
        leaveDuration =
          Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
      }
    }

    // -------------------------------------------
    // 4. FINAL PAYLOAD
    // -------------------------------------------
    const payload = {
      type,
      startDate: leaveStartDate,
      endDate: leaveEndDate,
      duration: leaveDuration,
      remark,
      emergencyReason: type === "Emergency Leave" ? emergencyReason : null,
      documentUrl,
      activityId:
        type === "Tournament Leave" || type === "Event Leave"
          ? selectedActivity
          : null,
      activityName,
    };

    // -------------------------------------------
    // 5. CREATE LEAVE THROUGH BACKEND
    // -------------------------------------------
    const res = await fetch(`${API_BASE}/api/player/leave/create`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json();

    if (data.success) {
      showToast("Leave applied successfully", "success");
      navigate("/player-attendance");
    } else {
      showToast(data.message || "Failed to apply leave", "error");
    }
  }


  // ------------------------------------------------------------
  // FORMAT DATE RANGE
  // ------------------------------------------------------------
  function formatRange(a) {
    const s = new Date(a.startDate).toLocaleDateString("en-GB");
    const e = new Date(a.endDate).toLocaleDateString("en-GB");
    return `${s} - ${e}`;
  }

  return (
    <MainContentLayout>
      <PageHeader title="Apply Leave" backTo="/player-attendance" />

      <div className="space-y-4 p-2 text-left">

        {/* LEAVE TYPE */}
        <div>
          <label className="block mb-1 font-medium">
            Leave Type <span className="text-red-500 italic text-xs">*Required</span>
          </label>
          <select
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              setSelectedActivity("");
            }}
            className="border p-2 rounded w-full"
          >
            <option value="">Select Leave Type</option>
            <option>Medical Leave</option>
            <option>Tournament Leave</option>
            <option>Event Leave</option>
            <option>Emergency Leave</option>
          </select>
        </div>

        {/* MEDICAL / EMERGENCY */}
        {(type === "Medical Leave" || type === "Emergency Leave") && (
          <>
            <div>
              <label className="block mb-1 font-medium">
                Start Date <span className="text-red-500 italic text-xs">*Required</span>
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStart(e.target.value)}
                className="border p-2 rounded w-full"
              />
            </div>

            <div>
              <label className="block mb-1 font-medium">
                Duration (days) <span className="text-red-500 italic text-xs">*Required</span>
              </label>
              <input
                type="number"
                min="1"
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                className="border p-2 rounded w-full"
              />
            </div>

            <div>
              <label className="block mb-1 font-medium">
                End Date <span className="text-red-500 italic text-xs">*Required</span>
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEnd(e.target.value)}
                className="border p-2 rounded w-full"
              />
            </div>

            {type === "Medical Leave" && (
              <div>
                <label className="block mb-2 font-medium">
                  Upload Document <span className="text-red-500 italic text-xs">*Required</span>
                </label>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={(e) => setFile(e.target.files[0])}
                />
              </div>
            )}

            {type === "Emergency Leave" && (
              <div>
                <label className="block mb-1 font-medium">
                  Emergency Reason <span className="text-red-500 italic text-xs">*Required</span>
                </label>
                <textarea
                  value={emergencyReason}
                  onChange={(e) => setEmergencyReason(e.target.value)}
                  className="border rounded p-2 w-full placeholder:italic placeholder:text-gray-400"
                  placeholder="Emergency reason..."
                />
              </div>
            )}
          </>
        )}

        {/* TOURNAMENT / EVENT */}
        {(type === "Tournament Leave" || type === "Event Leave") && (
          <div>
            <label className="block mb-1 font-medium">
              Select Activity <span className="text-red-500 italic text-xs">*Required</span>
            </label>
            <ActivityDropdown
              activities={activities.filter(act => act.type === (type === "Tournament Leave" ? "tournament" : "event"))}
              selectedActivity={selectedActivity}
              setSelectedActivity={setSelectedActivity}
            />
          </div>
        )}

        {/* GENERAL REMARK */}
        <div>
          <label className="block mb-1 font-medium">
            Remarks <span className="italic text-gray-400 text-xs">(Optional)</span>
          </label>
          <textarea
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            className="border p-2 rounded w-full placeholder:italic placeholder:text-gray-400"
            placeholder="Remark"
          />
        </div>

        {/* SUBMIT BUTTON */}
        <button
          onClick={submitLeave}
          className="bg-blue-600 text-white px-4 py-2 rounded w-full"
        >
          Submit
        </button>
      </div>

      <ToastContainer />
    </MainContentLayout>
  );
}
